import React from 'react';
import { useCollaboratorInvitations } from '../../../hooks/api/useCollaboratorInvitations';
import { getIntl } from '../../../utils/intl';
import { useLoadedUser } from '../../../hooks/api/useUser';
import getName from '../../../utils/getName';
import { ButtonPromptRegistrationCodeConfiguration } from '../../shared/RegistrationCode';
import { ActionButtonLayout } from '../../v2BaseComponents/ActionButtonLayout';
import { AnimateLoadedContent } from '../../v2BaseComponents/Loader';
import MainContentContainer from '../../v2BaseComponents/MainContentContainer';
import Stack, { ContentStack } from '../../v2BaseComponents/Stack';
import InfoIcon from '../../v2BaseComponents/InfoIcon';
import Text from '../../v2BaseComponents/Text';
import ListItem from '../../v2BaseComponents/ListItem';

const intl = getIntl('RegistrationSuccess', {
    yourAccountIsReady: 'Your account is ready.',
    welcome: 'Welcome, {name}',
    pendingInvitationText: `You have a pending Invitation from {inviterName} to access {profileName}'s Medical Profile. On the next page you will need to accept that invitation to get started.`,
    nextYouWillBeAbleTo: 'To get started we recommend you:',
    bullet1: 'Create a Medical Profile',
    bullet2: 'Connect and download Patient Portal data',
    bullet3: 'Edit the information if needed',
    bullet4: 'Invite a trusted Collaborator',
    letsBegin: `Let's Begin`,
});

export type UserRegistrationWelcomeOwnProps = {
    isLoading: boolean;
    onContinue: () => void;
};

export type UserRegistrationWelcomeProps = UserRegistrationWelcomeOwnProps;

const RegistrationSuccess: React.FC<UserRegistrationWelcomeProps> = ({ isLoading, onContinue }) => {
    const { user } = useLoadedUser({});
    const { isLoadingInvitations, invitations } = useCollaboratorInvitations({});

    const pendingInvitation = invitations?.[0];

    return (
        <AnimateLoadedContent isLoading={isLoading || isLoadingInvitations}>
            {() => (
                <ActionButtonLayout
                    scrollable
                    whiteBackground
                    actions={[
                        {
                            icon: 'CheckCircle',
                            title: intl('letsBegin'),
                            onPress: onContinue,
                            testID: 'registration-lets-begin',
                            isPrimary: true,
                        },
                    ]}
                >
                    <MainContentContainer>
                        <ContentStack>
                            <InfoIcon
                                alertIconProps={{
                                    variant: 'success',
                                    name: 'Check',
                                }}
                                title={intl('welcome', { name: user.preferredName || user.firstName })}
                                subText={intl('yourAccountIsReady')}
                            />
                            <Stack horizontal justify="center" fullWidth>
                                <Stack vertical align="flex-start" style={{ maxWidth: 320 }} fullWidth>
                                    {pendingInvitation ? (
                                        <Text variant="body-1">
                                            {intl('pendingInvitationText', {
                                                inviterName: getName({
                                                    firstName: pendingInvitation.invitedByFirstName,
                                                    lastName: pendingInvitation.invitedByLastName,
                                                }),
                                                profileName: getName(pendingInvitation.medicalProfile),
                                            })}
                                        </Text>
                                    ) : null}
                                    <Text variant="body-1">{intl('nextYouWillBeAbleTo')}</Text>
                                    {(['bullet1', 'bullet2', 'bullet3', 'bullet4'] as const).map((_, index) => (
                                        <ListItem unordered variant="body-1" key={_} index={index}>
                                            {intl(_)}
                                        </ListItem>
                                    ))}
                                </Stack>
                            </Stack>
                            <ButtonPromptRegistrationCodeConfiguration />
                        </ContentStack>
                    </MainContentContainer>
                </ActionButtonLayout>
            )}
        </AnimateLoadedContent>
    );
};

export default React.memo(RegistrationSuccess);
